import { Fragment, ReactElement, useState } from 'react'
import { Formik } from 'formik'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'
import { isFinite, isNumber } from 'lodash'

import { getUnprocessableErrors } from 'utils/error'
import { Button } from 'components/v2/Button'
import Card from '@10xLabs/react-web/components/Card'
import { Input } from 'components/Input'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import InputAddon from '@10xLabs/react-web/components/InputAddon'
import { Label } from 'components/v2/Label'
import { FormattedDuration } from '@10xLabs/react-web/components/FormattedDuration'
import { newSegmentSchema } from './schema'
import { round } from 'utils/math'
import { GTSegment, GTSegmentCompanyLineSpec } from 'gql/types.generated'
import Heading from 'components/v2/Heading'

const messages = defineMessages({
  distancePlaceholder: {
    id: 'segments.new.form.distance.placeholder',
    defaultMessage: 'e.g.: 100'
  },
  durationPlaceholder: {
    id: 'segments.new.form.duration.select',
    defaultMessage: 'e.g.: 60'
  },
  place: {
    id: 'segments.new.form.formattedPlace',
    defaultMessage: '{place} - {city} ({stateCode})'
  },
  realDistancePlaceholder: {
    id: 'segments.new.form.realDistance.placeholder',
    defaultMessage: 'e.g.: 100'
  }
})

const StyledDuration = styled(FormattedDuration)<TSFixMe>`
  justify-content: flex-end;
  margin-top: 5px;
  font-style: italic;
`

const StyledNumberInput = styled(InputAddon.Input)`
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

const CompanyLineSpecForm = (props: TSFixMe): ReactElement => {
  const { values, touched, errors, handleChange, handleBlur, segment, companyLineSpecIndex } = props

  const intl = useIntl()
  const [isDistanceFocused, setDistanceFocus] = useState(false)
  const [isDurationFocused, setDurationFocus] = useState(false)
  const [isRealDistanceFocused, setRealDistanceFocus] = useState(false)

  const companyLineSpec = segment.companyLineSpecs[companyLineSpecIndex]
  const companyLineSpecValue = values.companyLineSpecs[companyLineSpecIndex]
  const companyLineSpecTouched = touched.companyLineSpecs?.[companyLineSpecIndex]
  const companyLineSpecErrors = errors.companyLineSpecs?.[companyLineSpecIndex]

  return (
    <>
      <Row>
        <Column>
          <Heading.Body bold>{companyLineSpec.companyLine.name}</Heading.Body>
        </Column>
      </Row>
      <Row>
        <Column width="25%">
          <label>
            <Label>
              <FormattedMessage id="segments.new.form.distance.label" defaultMessage="Distance" />
            </Label>
            <InputAddon.Wrapper
              touched={companyLineSpecTouched?.distance}
              errors={companyLineSpecErrors?.distance}
              isFocused={isDistanceFocused}
            >
              <StyledNumberInput
                id={`companyLineSpecs.${companyLineSpecIndex}.distance`}
                onChange={handleChange}
                onBlur={(event: TSFixMe): TSFixMe => {
                  handleBlur(event)
                  setDistanceFocus(false)
                }}
                onFocus={(): TSFixMe => setDistanceFocus(true)}
                value={companyLineSpecValue.distance}
                placeholder={intl.formatMessage(messages.distancePlaceholder)}
                type="number"
                min="0"
                step="0.001"
              />
              <InputAddon.Addon>
                <FormattedMessage id="formatted.distance.unit.kilometers" defaultMessage="Km" />
              </InputAddon.Addon>
            </InputAddon.Wrapper>
            {companyLineSpecTouched?.distance && companyLineSpecErrors?.distance && (
              <ErrorMessage>{companyLineSpecErrors?.distance}</ErrorMessage>
            )}
          </label>
        </Column>
        <Column width="25%">
          <label>
            <Label>
              <FormattedMessage id="segments.new.form.realDistance.label" defaultMessage="Real distance" />
            </Label>
            <InputAddon.Wrapper
              touched={companyLineSpecTouched?.realDistance}
              errors={companyLineSpecErrors?.realDistance}
              isFocused={isRealDistanceFocused}
            >
              <StyledNumberInput
                id={`companyLineSpecs.${companyLineSpecIndex}.realDistance`}
                onChange={handleChange}
                onBlur={(event: FocusEvent): void => {
                  handleBlur(event)
                  setRealDistanceFocus(false)
                }}
                onFocus={(): TSFixMe => setRealDistanceFocus(true)}
                value={companyLineSpecValue.realDistance}
                placeholder={intl.formatMessage(messages.realDistancePlaceholder)}
                type="number"
                min="0"
                step="0.001"
              />
              <InputAddon.Addon>
                <FormattedMessage id="formatted.realDistance.unit.kilometers" defaultMessage="Km" />
              </InputAddon.Addon>
            </InputAddon.Wrapper>
            {companyLineSpecTouched?.realDistance && companyLineSpecErrors?.realDistance && (
              <ErrorMessage>{companyLineSpecErrors?.realDistance}</ErrorMessage>
            )}
          </label>
        </Column>
        <Column width="25%">
          <label>
            <Label>
              <FormattedMessage id="segments.new.form.duration.label" defaultMessage="Duration" />
            </Label>
            <InputAddon.Wrapper
              touched={companyLineSpecTouched?.duration}
              errors={companyLineSpecErrors?.duration}
              isFocused={isDurationFocused}
            >
              <StyledNumberInput
                id={`companyLineSpecs.${companyLineSpecIndex}.duration`}
                onChange={handleChange}
                onBlur={(event: TSFixMe): TSFixMe => {
                  handleBlur(event)
                  setDurationFocus(false)
                }}
                onFocus={(): TSFixMe => setDurationFocus(true)}
                value={companyLineSpecValue.duration}
                placeholder={intl.formatMessage(messages.durationPlaceholder)}
                type="number"
                min="1"
                step="1"
              />
              <InputAddon.Addon>
                <FormattedMessage id="formatted.duration.unit.minutes" defaultMessage="Min" />
              </InputAddon.Addon>
            </InputAddon.Wrapper>
            {!companyLineSpecErrors?.duration && companyLineSpecValue.duration ? (
              <StyledDuration duration={values.companyLineSpecs[companyLineSpecIndex].duration} />
            ) : null}
            {companyLineSpecTouched?.duration && companyLineSpecErrors?.duration ? (
              <ErrorMessage>{companyLineSpecErrors?.duration}</ErrorMessage>
            ) : null}
          </label>
        </Column>
      </Row>
    </>
  )
}
const NewSegmentForm = (props: TSFixMe): ReactElement => {
  const { handleSubmit, error, loading, segment } = props

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <Card.Content noHeader>
        <Row>
          <Column width="50%">
            <Input
              value={segment.origin.name}
              label={<FormattedMessage id="segments.new.form.origin.label" defaultMessage="Origin" />}
              readOnly
            />
          </Column>
          <Column width="50%">
            <Input
              value={segment.destination.name}
              label={<FormattedMessage id="segments.new.form.destination.label" defaultMessage="Destination" />}
              readOnly
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <Input
              value={segment.company.name}
              label={<FormattedMessage id="segments.new.form.company.label" defaultMessage="Company" />}
              readOnly
            />
          </Column>
        </Row>
        {(segment as GTSegment).companyLineSpecs.map((companyLineSpec, companyLineSpecIndex: number): ReactElement => {
          return (
            <CompanyLineSpecForm key={companyLineSpecIndex} companyLineSpecIndex={companyLineSpecIndex} {...props} />
          )
        })}
      </Card.Content>
      <Card.Footer>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="segments.new.form.error"
                defaultMessage="There was an error while updating your segment"
              />
            </ErrorMessage>
          </div>
        )}
        <Card.FooterActions>
          <Button as={NavLink} to="/segments" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" kind="primary" loading={loading}>
            <FormattedMessage id="dictionary.update" defaultMessage="Update" />
          </Button>
        </Card.FooterActions>
      </Card.Footer>
    </form>
  )
}

const mapDistanceToForm = (distance: number | null): number | '' => {
  return isNumber(distance) && isFinite(distance) ? round(distance / 1000, 3) : ''
}

export const NewSegment = ({ segment, onSubmit, error, loading }: TSFixMe): ReactElement => {
  const intl = useIntl()

  const initialValues = {
    companyLineSpecs: segment.companyLineSpecs.map(
      ({ duration, distance, realDistance, companyLine }: GTSegmentCompanyLineSpec): TSFixMe => {
        return {
          companyLine: companyLine,
          distance: mapDistanceToForm(distance ?? null),
          duration: duration || '',
          realDistance: mapDistanceToForm(realDistance ?? null)
        }
      }
    )
  }

  return (
    <Formik initialValues={initialValues} validationSchema={newSegmentSchema(intl)} onSubmit={onSubmit}>
      {(props): ReactElement => {
        return <NewSegmentForm {...props} segment={segment} error={error} loading={loading} />
      }}
    </Formik>
  )
}
